import { DEFAULT_FILTER_ROW } from '../data/contactsData';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export function formatContactTimestamp(epochMs) {
  if (!epochMs) return '—';
  const date = new Date(epochMs);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isActiveWithin30Days(epochMs) {
  if (!epochMs) return false;
  return Date.now() - epochMs <= THIRTY_DAYS_MS;
}

export function parseTagsInput(tagsInput) {
  if (Array.isArray(tagsInput)) return tagsInput.filter(Boolean);
  return String(tagsInput || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function resolveLeadStatusLabel(leadStatusValue) {
  const trimmed = String(leadStatusValue || '').trim();
  return trimmed || 'New Lead';
}

function resolveTagLabel(tagValue) {
  return String(tagValue || '').trim();
}

export function mapApiContactStatsToUi(apiStats) {
  return {
    totalContacts: apiStats.total_contacts.toLocaleString(),
    active30d: apiStats.active_30d.toLocaleString(),
    avgConversion: `${Number(apiStats.avg_conversion_percent).toFixed(1)}%`,
    growth: `+${apiStats.growth_weekly}/wk`,
  };
}

export function mapApiContactToUi(apiContact) {
  return {
    id: apiContact.customerId,
    name: apiContact.name,
    phone: apiContact.phone,
    email: apiContact.email || null,
    leadStatus: apiContact.leadStatus,
    source: apiContact.source || '',
    sourceCampaignId: null,
    tags: apiContact.tags || [],
    customAttributes: [],
    createdAtMs: apiContact.createdAt || null,
    createdAt: formatContactTimestamp(apiContact.createdAt),
    lastActiveAt: formatContactTimestamp(apiContact.lastActiveAt),
    isActive30d: isActiveWithin30Days(apiContact.lastActiveAt),
    activities: [],
  };
}

export function mapCreatedContactToUi(apiPayload, customerId) {
  return mapApiContactToUi({
    customerId,
    name: apiPayload.name,
    phone: apiPayload.phone,
    email: apiPayload.email || null,
    leadStatus: apiPayload.leadStatus,
    source: apiPayload.source || 'Manual',
    tags: apiPayload.tags || [],
    createdAt: Date.now(),
    lastActiveAt: null,
  });
}

export function buildListContactsPayload({
  search = '',
  limit = 10,
  leadStatus,
  tags,
  source,
  lastEvaluatedKey,
} = {}) {
  const payload = { limit };

  const trimmedSearch = search.trim();
  if (trimmedSearch) {
    payload.search = trimmedSearch;
  }
  if (leadStatus) {
    payload.leadStatus = leadStatus;
  }
  if (tags?.length) {
    payload.tags = tags;
  }
  if (source) {
    payload.source = source;
  }
  if (lastEvaluatedKey) {
    payload.lastEvaluatedKey = lastEvaluatedKey;
  }

  return payload;
}

function buildPositiveContactFilterPayload(uiFilters = []) {
  const payload = {};

  uiFilters.forEach((filter) => {
    if (!filter.value) return;

    if (filter.field === 'lead_status' && filter.operator === 'is') {
      payload.leadStatus = resolveLeadStatusLabel(filter.value);
    }
    if (filter.field === 'tags' && filter.operator === 'includes') {
      const tagLabel = resolveTagLabel(filter.value);
      if (!payload.tags) {
        payload.tags = [];
      }
      if (!payload.tags.includes(tagLabel)) {
        payload.tags.push(tagLabel);
      }
    }
  });

  return payload;
}

/** Maps advanced filter rows to LIST_CONTACTS payload (leadStatus, tags). */
export function buildListContactsFilterPayload(uiFilters = []) {
  return buildPositiveContactFilterPayload(uiFilters);
}

export function isApiContactRecord(item) {
  return Boolean(item?.customerId);
}

export function buildAddContactPayload(form) {
  const payload = {
    name: form.name.trim(),
    phone: form.phone.trim(),
    leadStatus: resolveLeadStatusLabel(form.leadStatus),
    tags: parseTagsInput(form.tags),
    source: 'Manual',
  };

  const email = form.email.trim();
  if (email) {
    payload.email = email;
  }

  return payload;
}

export function buildUpdateContactPayload({ contact, email, leadStatus, tags }) {
  const payload = {
    customerId: contact.id,
    name: contact.name,
    leadStatus: String(leadStatus || '').trim() || contact.leadStatus,
    tags: parseTagsInput(tags),
    source: contact.source || 'Manual',
  };

  const trimmedEmail = (email || '').trim();
  if (trimmedEmail) {
    payload.email = trimmedEmail;
  }

  return payload;
}

function resolveLeadStatusUiValue(leadStatusLabel) {
  return leadStatusLabel;
}

function resolveTagUiValue(tagLabel) {
  return tagLabel;
}

export function mapApiSegmentFiltersToUiRows(apiFilters = {}) {
  const rows = [];

  if (apiFilters.leadStatus) {
    rows.push({
      field: 'lead_status',
      operator: 'is',
      value: resolveLeadStatusUiValue(apiFilters.leadStatus),
      attributeKey: '',
    });
  }

  if (apiFilters.tags?.length) {
    apiFilters.tags.forEach((tag) => {
      rows.push({
        field: 'tags',
        operator: 'includes',
        value: resolveTagUiValue(tag),
        attributeKey: '',
      });
    });
  }

  return rows.length ? rows : [{ ...DEFAULT_FILTER_ROW }];
}

export function mapApiSegmentToUi(apiSegment) {
  return {
    id: apiSegment.segmentId,
    name: apiSegment.name,
    filters: mapApiSegmentFiltersToUiRows(apiSegment.filters),
    createdAtMs: apiSegment.createdAt || null,
    createdAt: formatContactTimestamp(apiSegment.createdAt),
  };
}

export function buildApiSegmentFiltersPayload(uiFilters = []) {
  return buildPositiveContactFilterPayload(uiFilters);
}

export function buildCreateSegmentPayload({ name, uiFilters }) {
  return {
    name: name.trim(),
    filters: buildApiSegmentFiltersPayload(uiFilters),
  };
}
